import React, { useState } from "react";
import { SubmitHandler, useForm, useFormState } from "react-hook-form";

import {
  Field,
  FieldHelper,
  FieldLabel,
  FormCard,
  FormPanel,
  discardCopyOf,
} from "../../../../components/FormPanel";
import { SelectForm } from "../../../../components/Input";
import { notify } from "../../../../components/Notification";
import {
  ErrorResponse,
  getErrorMessages,
  useSetInstrumentSourceCountryMutation,
} from "../../../../services";
import { StaticInstrument } from "./styles";
import {
  countryOptionFor,
  FORM_ID,
  instrumentLabelOf,
  NOT_SET_OPTION,
  SOURCE_COUNTRY_HELPER,
  SOURCE_COUNTRY_OPTIONS,
} from "./constants";
import { MobileSourceCountryForm } from "./MobileSourceCountryForm";
import { SourceCountryFormValues, SourceCountryPanelProps } from "./types";

/** Admin-only: the country lives on the shared instrument catalogue, so it changes every holder's tax report. */
export const SourceCountryPanel: React.FC<SourceCountryPanelProps> = ({
  isOpen,
  onClose,
  instrument,
}) => {
  const [panelError, setPanelError] = useState<string | null>(null);
  const [setSourceCountry] = useSetInstrumentSourceCountryMutation();
  const defaultCountry = countryOptionFor(instrument.sourceCountry);

  const form = useForm<SourceCountryFormValues>({
    defaultValues: { country: defaultCountry },
  });
  const { control, getValues } = form;
  const { isSubmitting } = useFormState({ control });

  const onSubmit: SubmitHandler<SourceCountryFormValues> = async (values) => {
    setPanelError(null);
    const sourceCountry = values.country?.value || null;
    try {
      await setSourceCountry({
        instrumentId: instrument.id,
        sourceCountry,
      }).unwrap();
      notify.success(
        sourceCountry ? "Source country saved" : "Source country cleared",
      );
      onClose();
    } catch (error: unknown) {
      setPanelError(getErrorMessages(error as ErrorResponse).join(" "));
    }
  };

  const buildDiscardDetails = () => {
    const country = getValues("country") ?? NOT_SET_OPTION;
    return {
      title: instrumentLabelOf(instrument),
      subtitle: `Source country: ${country.label}`,
    };
  };

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      error={panelError}
      ariaLabel="Set source country"
      eyebrow="Tax reporting"
      title="Set source country"
      form={form}
      formId={FORM_ID}
      onSubmit={onSubmit}
      submitLabel="Save country"
      discard={{
        ...discardCopyOf(true, "source country"),
        details: buildDiscardDetails,
      }}
      mobile={{
        title: "Set source country",
        submitLabel: "Save",
        fields: (
          <MobileSourceCountryForm
            control={control}
            instrument={instrument}
            defaultCountry={defaultCountry}
            isSubmitting={isSubmitting}
          />
        ),
      }}
    >
      <FormCard>
        <Field>
          <FieldLabel as="span">Instrument</FieldLabel>
          <StaticInstrument>{instrumentLabelOf(instrument)}</StaticInstrument>
        </Field>
        <Field>
          <FieldLabel htmlFor="source-country">Source country</FieldLabel>
          <SelectForm
            id="source-country"
            name="country"
            control={control}
            required={false}
            defaultValue={defaultCountry}
            options={SOURCE_COUNTRY_OPTIONS}
            disabled={isSubmitting}
          />
          <FieldHelper>{SOURCE_COUNTRY_HELPER}</FieldHelper>
        </Field>
      </FormCard>
    </FormPanel>
  );
};
